// The Estate, Fleet and repository a tab is acting in, read from its address.
//
// The address is the only place the scope is held: nothing else is kept beside
// it in memory. A link therefore carries everything needed to open the same
// scope, and each tab acts in the scope its own address names whatever another
// tab does. The one thing stored is the reader's last Estate
// (lib/client/last-estate.ts), which only fills an address that names none and
// is dropped at sign-out unless the reader asked for it to be remembered.
//
// Billing, quotas and feature gates resolve against an Estate, and the API
// refuses rather than guesses for anyone who belongs to more than one, so every
// request names the one in the address (see scope-headers.ts). Naming an Estate
// grants nothing: the API checks membership before honouring it.
//
// Sits under api-client rather than with the session helpers so the request
// builder can reach it without a cycle.

/** The query keys a page's address spells its scope with. */
export const SCOPE_SEARCH_KEYS = {
  estate: "estate",
  fleet: "fleet",
  repository: "repo",
} as const;

/** The value of a level that is not narrowed: All Fleets, All repositories. */
export const ALL_IN_SCOPE = "all";

export type AddressScope = {
  estateId: string;
  fleetId: string | null;
  repositoryId: string | null;
};

// A Fleet's own pages name the Estate and Fleet in the path rather than the
// query, and a repository's pages name the repository after them.
const FLEET_PATH = /^\/estate\/([^/]+)\/fleet\/([^/]+)\//;
const REPOSITORY_PATH =
  /^\/estate\/[^/]+\/fleet\/[^/]+\/repositories\/([^/]+)(?:\/|$)/;
// Fleet pages under /repositories/ that are not one repository.
const NOT_A_REPOSITORY = new Set(["upload", "add"]);

function level(value: string | null): string | null {
  return value && value !== ALL_IN_SCOPE ? value : null;
}

function repositoryInPath(pathname: string): string | null {
  const id = REPOSITORY_PATH.exec(pathname)?.[1];
  if (!id) return null;
  const decoded = decodeURIComponent(id);
  return NOT_A_REPOSITORY.has(decoded) ? null : decoded;
}

/**
 * The scope an address names, or null when it names none. A Fleet or
 * repository means nothing without the Estate holding it, so a query naming no
 * Estate names no scope at all.
 */
export function scopeInAddress(
  pathname: string,
  search: string,
): AddressScope | null {
  const params = new URLSearchParams(search);
  const repositoryId =
    repositoryInPath(pathname) ??
    level(params.get(SCOPE_SEARCH_KEYS.repository));
  const path = FLEET_PATH.exec(pathname);
  if (path)
    return {
      estateId: decodeURIComponent(path[1]),
      fleetId: decodeURIComponent(path[2]),
      repositoryId,
    };
  const estateId = params.get(SCOPE_SEARCH_KEYS.estate);
  if (!estateId) return null;
  return {
    estateId,
    fleetId: level(params.get(SCOPE_SEARCH_KEYS.fleet)),
    repositoryId,
  };
}

type ScopeLocation = { pathname: string; search: string };
let routerLocation: (() => ScopeLocation) | undefined;

/** Reads scope from the same committed router location used by rendered views. */
export function bindScopeLocation(read: () => ScopeLocation): () => void {
  routerLocation = read;
  return () => {
    if (routerLocation === read) routerLocation = undefined;
  };
}

/** The scope this tab's address names, read when it is needed. */
export function readAddressScope(): AddressScope | null {
  if (typeof window === "undefined") return null;
  const location = routerLocation?.() ?? window.location;
  return scopeInAddress(location.pathname, location.search);
}

export function readScopedEstateId(): string | null {
  return readAddressScope()?.estateId ?? null;
}

export function readScopedFleetId(): string | null {
  return readAddressScope()?.fleetId ?? null;
}
